import getStoreProperty from "components/ProjectNonSpecificComponents/DevTools/Functions/getStoreProperty"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import PreferencesCheckbox from "components/ProjectSpecificComponents/Elements/Global/atoms/PreferencesCheckbox"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import parseDataForReactSelect from "components/ProjectSpecificComponents/Functions/parseDataForReactSelect"
import {
	IMainField,
	TItemTypeFields,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import {
	isReactSelectSingleOption,
	TReactSelectOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import {
	IFormsConfig,
	IItemOfTypeLevels,
	INewRequestFormField,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import { has } from "lodash"
import { observer } from "mobx-react"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import FormField, { TFormFieldDirection } from "../FormField"

interface Props {
	firstLevel: boolean
	itemOfTypeLevels: IItemOfTypeLevels
	onChangeFormData: (data: Partial<INewRequestFormField>) => void
	picklistsForItem: TReactSelectOption[]
}

const ToolFieldPropertyIOTLevels = observer(
	({ firstLevel, itemOfTypeLevels, onChangeFormData, picklistsForItem }: Props) => {
		const { isMobile } = useScreenType()
		const { t } = useTranslation()
		const formStore = useFormStore()
		const formFieldDirection: TFormFieldDirection = isMobile ? "Horizontal" : "Vertical"
		const formSectionContainerStyle: React.CSSProperties = isMobile ? {} : { maxWidth: "240px" }
		const { newRequestLanguage } = formStore
		const {
			isPicklistFirstSetSecond,
			levelOneEnabled,
			levelTwoEnabled,
			firstLevelFieldNameLV = "",
			firstLevelFieldNameEN = "",
			secondLevelFieldNameLV = "",
			secondLevelFieldNameEN = "",
			picklistFieldId,
			stringFieldId,
		} = itemOfTypeLevels

		const levelIsPicklist: boolean =
			(firstLevel && isPicklistFirstSetSecond) || (!firstLevel && !isPicklistFirstSetSecond)

		// Just small non-reusable functions to make conditionaling easier
		const isStringType = (str: string) => str === "STRING"

		const fieldIsUsed = (id: string) => {
			if (!formStore.selectedFormsConfig) return false

			const { userDepField, userEmailField, userIdField, userMobField, userNameField, itemType } =
				formStore.selectedFormsConfig

			let fieldIsUsedInCnfg =
				itemType.id === id ||
				userDepField?.fieldId === id ||
				userEmailField?.fieldId === id ||
				userIdField?.fieldId === id ||
				userMobField?.fieldId === id ||
				userNameField?.fieldId === id

			let fieldIsUsedInForm = formStore.selectedFormsConfig.newRequestForm.some(
				({ mainFieldKey }) => mainFieldKey == id
			)

			return fieldIsUsedInCnfg || fieldIsUsedInForm
		}

		const stringFieldPicklist: TReactSelectOption[] = useMemo(() => {
			const stringFields = formStore.mainFieldsData?.filter(({ fieldType }) =>
				isStringType(fieldType)
			)

			return stringFields
				? stringFields.map(({ fieldId, name, readOnly }) => ({
						value: fieldId,
						label: name,
						isDisabled: readOnly || fieldIsUsed(fieldId),
				  }))
				: []
		}, [formStore.mainFieldsData, formStore?.selectedFormsConfig?.newRequestForm])

		const onCheckLevel = (checked: boolean) => {
			if (firstLevel && !checked) {
				return onChangeFormData({
					itemOfTypeLevels: {
						...itemOfTypeLevels,
						...defaultLevelOneFalse,
					},
				})
			}
			if (!firstLevel && !checked) {
				return onChangeFormData({
					itemOfTypeLevels: {
						...itemOfTypeLevels,
						...defaultLevelTwoFalse,
					},
				})
			}
			return onChangeFormData({
				itemOfTypeLevels: {
					...itemOfTypeLevels,
					[`${firstLevel ? "levelOneEnabled" : "levelTwoEnabled"}`]: checked,
				},
			})
		}

		return (
			<HyperDiv
				id="field-IOT-two-levels"
				direction={isMobile ? "column" : "row"}
				className="w-100-children"
				centeredY
				padding="10px 0px"
				margin="0px 0px 10px 0px"
				style={{ background: "var(--light-light-gray)" }}
			>
				<HyperDiv padding="5px 10px" style={{ width: isMobile ? "100%" : "fit-content" }}>
					<FormField
						direction={formFieldDirection}
						label={`${t("forms.field-label.extra")}: ${firstLevel ? 1 : 2}. ${t(
							"forms.field-label.level"
						)}`}
						placeholderValue=""
						editable
					>
						<PreferencesCheckbox
							checked={firstLevel ? levelOneEnabled : levelTwoEnabled}
							readOnly={false}
							onCheck={onCheckLevel}
						/>
					</FormField>
				</HyperDiv>

				{((firstLevel && levelOneEnabled) || (!firstLevel && levelTwoEnabled)) && (
					<>
						<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
							<FormField
								direction={formFieldDirection}
								label={`${t("rawText.fieldName")} *`}
								placeholderValue=""
								editable
							>
								<Input
									style={{
										width: "100%",
										fontSize: 14,
									}}
									value={
										firstLevel
											? newRequestLanguage == "en"
												? firstLevelFieldNameEN
												: firstLevelFieldNameLV
											: newRequestLanguage == "en"
											? secondLevelFieldNameEN
											: secondLevelFieldNameLV
									}
									onChange={(e) =>
										onChangeFormData({
											itemOfTypeLevels: {
												...itemOfTypeLevels,
												[`${
													firstLevel
														? `firstLevelFieldName${newRequestLanguage.toUpperCase()}`
														: `secondLevelFieldName${newRequestLanguage.toUpperCase()}`
												}`]: e.target.value,
											},
										})
									}
								/>
							</FormField>
						</HyperDiv>
						<HyperDiv padding="0px 10px" style={{ width: isMobile ? "100%" : "fit-content" }}>
							<FormField
								direction={formFieldDirection}
								label={t("forms.field-label.is-set")}
								placeholderValue=""
								editable
							>
								<PreferencesCheckbox
									checked={firstLevel ? !isPicklistFirstSetSecond : isPicklistFirstSetSecond}
									readOnly={false}
									onCheck={(checked) =>
										onChangeFormData({
											itemOfTypeLevels: {
												...itemOfTypeLevels,
												isPicklistFirstSetSecond: !isPicklistFirstSetSecond,
											},
										})
									}
								/>
							</FormField>
						</HyperDiv>
						{/* // ja ir sets, tad jasuta label nevis value */}
						{levelIsPicklist && (
							<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
								<FormField
									direction={formFieldDirection}
									label={`${t("forms.field-label.picklist-field")} *`}
									placeholderValue=""
									editable
								>
									<Select
										valueKey={picklistFieldId}
										options={picklistsForItem ?? []}
										onChange={(selectedOption) => {
											onChangeFormData({
												itemOfTypeLevels: {
													...itemOfTypeLevels,
													picklistFieldId: isReactSelectSingleOption(selectedOption)
														? selectedOption.value
														: undefined,
												},
											})
										}}
									/>
								</FormField>
							</HyperDiv>
						)}
						{!levelIsPicklist && (
							<HyperDiv padding="0px 10px" style={formSectionContainerStyle}>
								<FormField
									direction={formFieldDirection}
									label={`${t("forms.field-label.for-save-field")} *`}
									placeholderValue=""
									editable
								>
									<Select
										valueKey={stringFieldId}
										options={stringFieldPicklist ?? []}
										onChange={(selectedOption) => {
											onChangeFormData({
												itemOfTypeLevels: {
													...itemOfTypeLevels,
													stringFieldId: isReactSelectSingleOption(selectedOption)
														? selectedOption.value
														: undefined,
												},
											})
										}}
									/>
								</FormField>
							</HyperDiv>
						)}
					</>
				)}
			</HyperDiv>
		)
	}
)

export default ToolFieldPropertyIOTLevels

export const defaultLevelOneFalse = {
	isPicklistFirstSetSecond: false,
	levelOneEnabled: false,
	levelTwoEnabled: false,
	firstLevelFieldNameLV: "",
	firstLevelFieldNameEN: "",
	secondLevelFieldNameLV: "",
	secondLevelFieldNameEN: "",
	picklistFieldId: "",
	stringFieldId: "",
}
export const defaultLevelTwoFalse = {
	isPicklistFirstSetSecond: false,
	levelTwoEnabled: false,
	secondLevelFieldNameLV: "",
	secondLevelFieldNameEN: "",
	picklistFieldId: "",
}
